import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore, type ThemeMode } from '../../store/useThemeStore';

const MODES: { value: ThemeMode; labelKey: string }[] = [
  { value: 'light', labelKey: 'appearance.light' },
  { value: 'system', labelKey: 'appearance.system' },
  { value: 'dark', labelKey: 'appearance.dark' },
];

// gap-1 (0.25rem) rendered at the root font size. Used to reconstruct the width
// three equal columns would need; kept in sync with the `gap-1`/`p-1` classes.
const GAP_PX = 4;
const PAD_PX = 4;

interface AppearanceControlProps {
  className?: string;
}

/**
 * Segmented light / system / dark control bound to the shared useThemeStore.
 *
 * All-or-nothing responsiveness: the three options are either all in one row or
 * all stacked vertically — never the 2-then-1 split that a `repeat(auto-fit …)`
 * grid produces. Pure CSS can't express that here: the container width is a
 * fixed px (the drawer / card), while "runs out of room" is driven by the user's
 * text SIZE (rem), so no width-based media/container query fires on it. Instead
 * an invisible, content-sized measurer reports the natural width of the widest
 * label; if three equal columns of that width wouldn't fit, we switch the grid
 * from `grid-cols-3` to `grid-cols-1`. A ResizeObserver on both the container
 * (width changes) and the measurer (text-size / language changes) keeps it live,
 * and useLayoutEffect applies the decision before paint so there's no flicker.
 *
 * Shared by the guest drawer (GuestMenu) and the Settings page so the two stay
 * in lockstep. Reads/writes the theme via useThemeStore (persists to
 * localStorage); no auth required, so it works for logged-out guests.
 *
 * Selected state is a solid brand fill rather than an elevated neutral surface:
 * Ionic's surface steps and --card/--background invert their relative lightness
 * between light and dark mode, so a "lighter pill on darker track" look can't be
 * expressed with one set of neutral tokens. The brand fill + white text reads as
 * "on" in both themes (and matches the Sign In CTA in the same drawer).
 *
 * i18n: `common` namespace only — GuestMenu mounts outside the page Suspense
 * boundary, so this must not pull in a lazy namespace (see GuestMenu's note).
 */
export const AppearanceControl: React.FC<AppearanceControlProps> = ({ className }) => {
  const { t } = useTranslation('common');
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [stacked, setStacked] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const evaluate = () => {
      const items = Array.from(measure.children) as HTMLElement[];
      if (items.length === 0) return;
      // Widest label at its natural (unconstrained) size — the buttons carry the
      // same padding/font, so this is the min width one of three equal columns
      // needs. Three of those + the two inter-column gaps is the row's ask.
      const widest = Math.max(...items.map((el) => el.offsetWidth));
      const needed = widest * MODES.length + GAP_PX * (MODES.length - 1);
      const available = container.clientWidth - PAD_PX * 2;
      setStacked(needed > available);
    };

    evaluate();
    const ro = new ResizeObserver(evaluate);
    ro.observe(container);
    ro.observe(measure);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={t('appearance.title')}
      className={`relative grid gap-1 rounded-lg border border-border bg-surface-100 p-1 ${
        stacked ? 'grid-cols-1' : 'grid-cols-3'
      } ${className ?? ''}`}
    >
      {MODES.map(({ value, labelKey }) => {
        const selected = mode === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => setMode(value)}
            className={`whitespace-nowrap rounded-md px-3 py-2 text-center text-sm font-medium leading-tight transition-colors ${
              selected
                ? 'bg-brand-primary text-white shadow-sm'
                : 'text-muted-foreground hover:bg-surface-200 hover:text-foreground'
            }`}
          >
            {t(labelKey)}
          </button>
        );
      })}

      {/* Invisible measurer: the three labels laid out content-sized (not
          stretched), mirroring the buttons' padding/font. Absolutely positioned
          so it's not a grid track and never affects layout; it exists only to
          report each label's natural width to the effect above. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex gap-1"
      >
        {MODES.map(({ value, labelKey }) => (
          <span
            key={value}
            className="whitespace-nowrap px-3 py-2 text-sm font-medium leading-tight"
          >
            {t(labelKey)}
          </span>
        ))}
      </div>
    </div>
  );
};
